// CalendarEinstellungen.tsx — Zahnrad „Einstellungen für den Kalender" in der
// Top-Leiste von `/termine` (T-31). RdPopover nach dem Muster CalendarPicker.
// Einzige Einstellung bisher: die Zeitskala des Tag-/Wochenrasters (15 / 30 /
// 60 Minuten). Sie steuert Darstellung UND Einrasten beim Ziehen und wird im
// CalendarStateContext (localStorage `calendar.zeitskala`) gespeichert.

import React from "react";
import { REFINED } from "../risikoanalyseStep/tokens";
import { RdPopover } from "../redesign/table/RdPopover";
import { I } from "../redesign/kvpIcons";
import { useCalendarState } from "./CalendarStateContext";
import { ZEITSKALEN } from "./zeitskala";

const TITEL = "Einstellungen für den Kalender";

const CalendarEinstellungen: React.FC = () => {
    const { zeitskala, setZeitskala } = useCalendarState();

    return (
        <RdPopover
            width={260}
            align="end"
            dialogLabel={TITEL}
            trigger={({ toggle, open, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    onClick={toggle}
                    aria-expanded={open}
                    aria-label={TITEL}
                    title={TITEL}
                    style={triggerStyle(open)}
                >
                    {I.settings}
                </button>
            )}
        >
            {() => (
                <div style={panelStyle}>
                    <div id="kal-einst-zeitskala" style={labelStyle}>
                        Zeitskala
                    </div>
                    <div
                        role="radiogroup"
                        aria-labelledby="kal-einst-zeitskala"
                        style={segmentStyle}
                    >
                        {ZEITSKALEN.map((z) => {
                            const aktiv = z === zeitskala;
                            return (
                                <button
                                    key={z}
                                    type="button"
                                    role="radio"
                                    aria-checked={aktiv}
                                    onClick={() => setZeitskala(z)}
                                    style={optionStyle(aktiv)}
                                >
                                    {z} Min
                                </button>
                            );
                        })}
                    </div>
                    <div style={hinweisStyle}>
                        Gröbere Skala zeigt mehr Stunden auf einmal. Beim Ziehen rasten
                        Termine im gewählten Takt ein; im Termin selbst bleibt jede
                        Uhrzeit möglich.
                    </div>
                </div>
            )}
        </RdPopover>
    );
};

const triggerStyle = (open: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 36,
    minWidth: 36,
    padding: 0,
    borderRadius: 8,
    border: `1px solid ${open ? REFINED.aub : REFINED.line}`,
    background: REFINED.white,
    color: REFINED.ink,
    cursor: "pointer",
    boxShadow: open ? REFINED.focusRing : "none",
});

const panelStyle: React.CSSProperties = {
    background: REFINED.white,
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    boxShadow: REFINED.cardShadow,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
};

const labelStyle: React.CSSProperties = {
    color: REFINED.ink,
    font: `700 13px ${REFINED.fontUI}`,
};

const segmentStyle: React.CSSProperties = {
    display: "flex",
    border: `1px solid ${REFINED.line}`,
    borderRadius: 8,
    overflow: "hidden",
};

const optionStyle = (aktiv: boolean): React.CSSProperties => ({
    flex: 1,
    minHeight: 36,
    border: "none",
    background: aktiv ? REFINED.aub : "transparent",
    color: aktiv ? REFINED.white : REFINED.ink,
    font: `600 14px ${REFINED.fontUI}`,
    cursor: "pointer",
});

const hinweisStyle: React.CSSProperties = {
    color: REFINED.ink3,
    font: `400 12px ${REFINED.fontUI}`,
    lineHeight: 1.4,
};

export default CalendarEinstellungen;
